import { eventModal } from "@NextSkyTheme/modal";

// Tiroir « Comment ça marche » : s'ouvre sur le sommaire depuis un lien vers #<ancre du sommaire>, sur un panneau
// depuis un lien vers #<ancre d'un panneau> ; change de panneau par les cartes du sommaire, les onglets du haut et
// « Suivant » ; anime les schémas (deux vues, couche choisie).

const headerOffset = () =>
  (parseFloat(getComputedStyle(document.documentElement).getPropertyValue("--header-height")) || 0) + 16;

function setSide(diagram, side) {
  diagram.dataset.side = side;
  // Poids : à gauche les trois couches, la lumière s'arrête sur la membrane (2) ; à droite la seule couche (1).
  if (diagram.dataset.scenario === "weight") diagram.dataset.targetWin = side === "left" ? "2" : "1";
  requestAnimationFrame(() => placeRays(diagram));
  diagram.querySelectorAll("[data-kt-side]").forEach((button) => {
    const on = button.dataset.ktSide === side;
    button.setAttribute("aria-pressed", on ? "true" : "false");
    button.className = `kt-how-diagram__switch ${on ? button.dataset.on : button.dataset.off}`;
  });
}

function setLayer(diagram, layer) {
  diagram.dataset.layer = layer;
  diagram.querySelectorAll("[data-kt-layer]").forEach((button) => {
    const on = layer === "all" || button.dataset.ktLayer === layer;
    button.setAttribute("aria-pressed", on ? "true" : "false");
  });
}

// Les flèches qui frappent une couche s'arrêtent à son centre : celui de son image, sinon de la couche dessinée.
function placeRays(diagram) {
  const stage = diagram.querySelector(".kt-how-diagram__stage");
  if (!stage || !stage.offsetParent) return;
  const center = (n) => {
    // Le premier élément VISIBLE : avec le scénario poids, chaque côté a ses photos, et une bande à 0 px ne compte pas.
    const element = [...diagram.querySelectorAll(`.kt-how-diagram__photo--${n}, .kt-band--${n}`)].find(
      (e) => e.getBoundingClientRect().width > 0
    );
    if (!element) return null;
    const rect = element.getBoundingClientRect();
    return rect.left + rect.width / 2;
  };
  const win = diagram.querySelector(".kt-how-diagram__zone--window");
  const room = diagram.querySelector(".kt-how-diagram__zone--room");
  const targetWin = diagram.dataset.targetWin;
  const targetRoom = diagram.dataset.targetRoom;
  if (targetWin && win) {
    const x = center(targetWin);
    if (x !== null) diagram.style.setProperty("--kt-reach-win", `${win.getBoundingClientRect().left - x}px`);
  }
  if (targetRoom && room) {
    const x = center(targetRoom);
    if (x !== null) diagram.style.setProperty("--kt-reach-room", `${x - room.getBoundingClientRect().right}px`);
  }
}

function initDiagram(diagram) {
  if (diagram.dataset.ktReady) return;
  diagram.dataset.ktReady = "true";
  diagram.querySelectorAll("[data-kt-side]").forEach((button) =>
    button.addEventListener("click", () => setSide(diagram, button.dataset.ktSide))
  );
  diagram.querySelectorAll("[data-kt-layer]").forEach((button) =>
    button.addEventListener("click", () => setLayer(diagram, button.dataset.ktLayer))
  );
  diagram.querySelectorAll(".kt-how-diagram__photo img").forEach((image) => {
    if (!image.complete) image.addEventListener("load", () => placeRays(diagram), { once: true });
  });
  // Toucher l'image d'une couche la choisit aussi.
  diagram.querySelectorAll("[data-kt-photo]").forEach((photo) =>
    photo.addEventListener("click", () => setLayer(diagram, photo.dataset.ktPhoto))
  );
}

class KtHowDrawer extends HTMLElement {
  connectedCallback() {
    this.panels = [...this.querySelectorAll("[data-kt-how-panel]")];
    this.body = this.querySelector(".kt-how-drawer__body");
    this.summary = this.panels.length > 1 ? this.dataset.summary || "" : "";
    this.tabs = this.buildTabs();
    this.cards = this.buildCards();
    this.next = this.querySelector("[data-kt-how-next]");
    this.useTextFont();
    this.select(this.panels[0] && this.panels[0].dataset.anchor);
    this.setView(this.summary ? "summary" : "panel");

    this.tabs.forEach((tab) => tab.addEventListener("click", () => this.select(tab.dataset.ktHowTab)));
    this.cards.forEach((card) =>
      card.addEventListener("click", () => {
        this.show(card.dataset.ktHowOpen);
        this.focusTab();
      })
    );
    const back = this.querySelector("[data-kt-how-back]");
    if (back) back.addEventListener("click", () => this.backToSummary());
    if (this.next) this.next.addEventListener("click", () => (this.nextTarget ? this.show(this.nextTarget) : this.backToSummary()));
    this.querySelectorAll("[data-kt-how-go]").forEach((button) =>
      button.addEventListener("click", () => this.go(button.dataset.ktHowGo))
    );
    this.querySelectorAll("[data-kt-how-diagram]").forEach(initDiagram);
    // Échap ferme une story du panneau (son <dialog>), pas le tiroir avec elle : modal-overlay ferme le tiroir au keyup.
    this.addEventListener(
      "keydown",
      (event) => {
        if (event.key === "Escape") this.escInDialog = !!this.querySelector("dialog[open]");
      },
      true
    );
    this.addEventListener(
      "keyup",
      (event) => {
        if (event.key !== "Escape" || !this.escInDialog) return;
        this.escInDialog = false;
        event.stopPropagation();
      },
      true
    );

    this.onLinkClick = (event) => {
      const link = event.target.closest && event.target.closest('a[href*="#"]');
      if (!link || this.contains(link)) return;
      const url = new URL(link.getAttribute("href"), window.location.href);
      if (url.pathname !== window.location.pathname) return;
      const anchor = decodeURIComponent(url.hash.slice(1));
      if (!this.opens(anchor)) return;
      event.preventDefault();
      this.open(anchor);
    };
    document.addEventListener("click", this.onLinkClick);
    this.onResize = () => this.querySelectorAll(".kt-how-panel.is-active [data-kt-how-diagram]").forEach(placeRays);
    window.addEventListener("resize", this.onResize);

    // Éditeur de thème : sélectionner la section ouvre le sommaire, un bloc du tiroir son panneau.
    this.onBlockSelect = (event) => {
      const panel = event.target.closest && event.target.closest("[data-kt-how-panel]");
      if (panel && this.contains(panel)) this.show(panel.dataset.anchor);
    };
    this.onSectionSelect = (event) => {
      if (event.detail && event.detail.sectionId === this.dataset.sectionId) this.open(this.summary || this.current);
    };
    document.addEventListener("shopify:block:select", this.onBlockSelect);
    document.addEventListener("shopify:section:select", this.onSectionSelect);

    const hash = decodeURIComponent(window.location.hash.slice(1));
    if (this.opens(hash)) requestAnimationFrame(() => this.open(hash));
  }

  disconnectedCallback() {
    document.removeEventListener("click", this.onLinkClick);
    window.removeEventListener("resize", this.onResize);
    document.removeEventListener("shopify:block:select", this.onBlockSelect);
    document.removeEventListener("shopify:section:select", this.onSectionSelect);
  }

  buildTabs() {
    const list = this.querySelector(".kt-how-drawer__tabs");
    if (!list) return [];
    list.replaceChildren();
    if (this.panels.length < 2) {
      list.hidden = true;
      return [];
    }
    list.hidden = false;
    list.style.setProperty("--kt-how-tabs", this.panels.length);
    return this.panels.map((panel, index) => {
      const tab = document.createElement("button");
      tab.type = "button";
      tab.setAttribute("role", "tab");
      tab.dataset.ktHowTab = panel.dataset.anchor;
      tab.dataset.on = list.dataset.on;
      tab.dataset.off = list.dataset.off;
      if (list.dataset.numbers === "true") {
        const number = document.createElement("span");
        number.className = "kt-how-drawer__tab-num";
        number.textContent = String(index + 1);
        tab.append(number);
      }
      const label = document.createElement("span");
      label.className = "kt-how-drawer__tab-label";
      label.textContent = panel.dataset.label;
      tab.append(label);
      list.append(tab);
      return tab;
    });
  }

  // Une carte par panneau, copiée depuis son modèle (<template data-kt-how-card>) et numérotée dans l'ordre.
  buildCards() {
    const list = this.querySelector("[data-kt-how-cards]");
    if (!list || !this.summary) return [];
    list.replaceChildren();
    return this.panels
      .map((panel, index) => {
        const template = panel.querySelector("template[data-kt-how-card]");
        const card = template && template.content.firstElementChild && template.content.firstElementChild.cloneNode(true);
        if (!card) return null;
        const number = card.querySelector("[data-kt-how-num]");
        if (number) number.textContent = String(index + 1);
        list.append(card);
        return card;
      })
      .filter(Boolean);
  }

  // Les cartes et « Suivant » sont des boutons : le thème (Custom Font) y impose la police des boutons, en petites
  // capitales, que leur texte hérite. Le chiffre, la phrase et la ligne du haut reprennent la police d'un paragraphe.
  useTextFont() {
    const paragraph = this.querySelector(".kt-how-drawer__intro p, .kt-how-panel p") || document.querySelector("main p");
    if (!paragraph) return;
    const style = getComputedStyle(paragraph);
    this.style.setProperty("--kt-how-text-font", style.fontFamily);
    this.style.setProperty("--kt-how-text-weight", style.fontWeight);
  }

  has(anchor) {
    return Boolean(anchor) && this.panels.some((panel) => panel.dataset.anchor === anchor);
  }

  opens(anchor) {
    return this.has(anchor) || (Boolean(this.summary) && anchor === this.summary);
  }

  select(anchor) {
    if (!this.has(anchor)) return;
    this.current = anchor;
    this.panels.forEach((panel) => panel.classList.toggle("is-active", panel.dataset.anchor === anchor));
    this.tabs.forEach((tab) => {
      const on = tab.dataset.ktHowTab === anchor;
      tab.setAttribute("aria-selected", on ? "true" : "false");
      tab.className = `kt-how-drawer__tab ${on ? tab.dataset.on : tab.dataset.off}`;
    });
    this.updateNext();
    if (this.body) this.body.scrollTop = 0;
    requestAnimationFrame(() => this.querySelectorAll(".kt-how-panel.is-active [data-kt-how-diagram]").forEach(placeRays));
  }

  // « Suivant » : le panneau d'après ; après le dernier, le retour au sommaire (s'il y en a un et un libellé).
  updateNext() {
    if (!this.next) return;
    const index = this.panels.findIndex((panel) => panel.dataset.anchor === this.current);
    const following = this.panels[index + 1];
    const kicker = this.next.querySelector("[data-kt-how-next-kicker]");
    const label = this.next.querySelector("[data-kt-how-next-label]");
    if (following) {
      this.nextTarget = following.dataset.anchor;
      kicker.textContent = (this.next.dataset.kicker || "")
        .replace("[n]", String(index + 2))
        .replace("[total]", String(this.panels.length));
      label.textContent = following.dataset.label;
    } else {
      this.nextTarget = "";
      kicker.textContent = this.summary ? this.next.dataset.endKicker || "" : "";
      label.textContent = this.summary ? this.next.dataset.endLabel || "" : "";
    }
    kicker.hidden = !kicker.textContent;
    this.next.hidden = !label.textContent;
  }

  setView(view) {
    this.dataset.view = view;
    if (this.body) this.body.scrollTop = 0;
    if (view === "panel")
      requestAnimationFrame(() => this.querySelectorAll(".kt-how-panel.is-active [data-kt-how-diagram]").forEach(placeRays));
  }

  openModal() {
    // Option mobile du thème : sous 1025 px, la feuille du bas reçoit la poignée pour glisser et fermer.
    if (!this.classList.contains("active")) eventModal(this, "open", false, null, true);
  }

  open(anchor) {
    if (this.summary && anchor === this.summary) this.showSummary();
    else this.show(anchor);
  }

  show(anchor) {
    this.select(anchor || this.current);
    this.setView("panel");
    this.openModal();
  }

  showSummary() {
    if (!this.summary) return this.show(this.current);
    this.setView("summary");
    this.openModal();
  }

  // Retour au sommaire par le bouton du haut ou « Suivant » : le focus passe sur la carte du panneau quitté.
  backToSummary() {
    const index = this.panels.findIndex((panel) => panel.dataset.anchor === this.current);
    this.showSummary();
    const card = this.cards[index] || this.cards[0];
    if (card) requestAnimationFrame(() => card.focus({ preventScroll: true }));
  }

  focusTab() {
    const tab = this.tabs.find((item) => item.dataset.ktHowTab === this.current);
    if (tab) requestAnimationFrame(() => tab.focus({ preventScroll: true }));
  }

  go(target) {
    eventModal(this, "close");
    const product = document.querySelector('[id^="ProductInfo-"]');
    const element = (target === "dimensions" && document.querySelector(".sur-mesure-all-container")) || product;
    if (!element) return;
    setTimeout(() => {
      const top = element.getBoundingClientRect().top + window.scrollY - headerOffset();
      window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
    }, 350);
  }
}

if (!customElements.get("kt-how-drawer")) customElements.define("kt-how-drawer", KtHowDrawer);
